import { useState, useContext, useEffect } from 'react';

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { message, Spin, Table, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import {
  ColSize,
  ContentSection,
  Form,
  InputCurrencyForm,
  InputForm,
  InputNumberForm,
  RowSize,
  SelectForm,
} from '../../../../../../../shared/components';
import { Modal } from '@hdi-seguros/web-components.modal';
import { QuotationInfoContext } from '../..';

import QuotationService from '../../../../../../../shared/services/quotation';

import { Combo } from '../../../../../../../shared/interfaces';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../shared/utils';

import './index.css';
import { COLORS } from '../../../../../../../config/themes/colors';

interface Coverage {
  codigo: string;
  description: string;
  familyCode?: string;
  familyDescription?: string;
  key: string;
  riskCode: string;
  sumInsured: string;
  typeCoverage?: string;
}

interface CoveragesList {
  [key: string]: Coverage[];
}

interface ObjectFormSectionProps {
  coveragesListInfo: CoveragesList;
  numberComponent: string;
}

export const ObjectFormSection = ({
  coveragesListInfo,
  numberComponent,
}: ObjectFormSectionProps) => {
  const validateForm = new ValidateForm();

  const [coverageList, setCoverageList] = useState<Combo[]>([]);
  const [familyList, setFamilyList] = useState<any>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentTableIndex, setCurrentTableIndex] = useState<number>(1);
  const [isUploadObject, setIsUploadObject] = useState<boolean>(false);
  const [objectTable, setObjectTable] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [showObjectModal, setShowObjectModal] = useState<boolean>(false);

  const { quotationInfo } = useContext(QuotationInfoContext);
  
  const [objectsForm] = useForm();

  const MOCK_TABLE_COLUMNS_HEADER = [
    {
      title: 'Cobertura',
      dataIndex: `coverage-tab-${numberComponent}`,
      width: '20%',
    },
    {
      title: 'Descripción',
      dataIndex: `descriptionObject-tab-${numberComponent}`,
      width: '10%',
    },
    {
      title: 'Marca',
      dataIndex: `markObject-tab-${numberComponent}`,
      width: '15%',
    },
    {
      title: 'Modelo',
      dataIndex: `modelObject-tab-${numberComponent}`,
      width: '15%',
    },
    {
      title: 'Nro. Serie',
      dataIndex: `numberSerieObject-tab-${numberComponent}`,
      width: '15%',
    },
    {
      title: 'Detalle',
      dataIndex: `detailObject-tab-${numberComponent}`,
      width: '15%',
    },
    {
      title: 'Suma asegurada',
      dataIndex: `sumInsured-tab-${numberComponent}`,
      width: '10%',
      render: (value: string) => `$${formattedPrice(value)}`,
    },
    {
      title: 'Acción',
      dataIndex: 'action',
      className: 'action-column',
      render: (_: any, data: any) => {
        return (
          <ActionButton
            color='danger'
            icon={<DeleteOutlined />}
            label=''
            onClick={() => onDeleteBeneficiary(data.key)}
            type='secondary'
          />
        );
      },
    },
  ];

  const onDeleteBeneficiary = async (sequence: string) => {
    try {
      const deleteObject = {
        numeroCotizacion: Number(quotationInfo?.token),
        rama: Number(quotationInfo?.branch),
        articuloRamaSecuencia: Number(quotationInfo?.arse),
        componente: Number(numberComponent),
        riesgo: '103',
        cobertura: 1,
        idSecuencial: Number(sequence),
      };

      await QuotationService.postDeleteInsuredObject(deleteObject);

      onDeleteObjectOnTable(sequence);
    } catch (error) {}
  };

  const onDeleteObjectOnTable = (key: string) => {
    const filteredObjects = objectTable.filter(
      (object: any) => object.key !== key
    );

    setObjectTable(filteredObjects);
  };

  const addNewBeneficiaryOnTable = (formFields: any) => {
    const newObject = {
      key: currentTableIndex,
      ...formFields,
    };

    setObjectTable((prevValues: any) => [...prevValues, newObject]);
    setShowObjectModal(false);
    objectsForm.resetFields();
    setCoverageList([]);
  };

  const onAddNewObject = async () => {
    if (objectTable?.length === 99) {
      message.error('Se puede agregar como máximo 99 objetos.');
    }

    try {
      setCurrentTableIndex((prevValue) => prevValue + 1);

      await objectsForm.validateFields();

      const formFields = objectsForm.getFieldsValue();

      const familyValue = formFields[`family-tab-${numberComponent}`];

      const familyDescription = familyList?.find(
        (coverage: Combo) => String(coverage.value) === String(familyValue)
      );

      const coverageValue = formFields[`coverage-tab-${numberComponent}`];

      const coverageData = Object.values(coveragesListInfo)
        .flat()
        .find(
          (coverage) =>
            `${coverage.familyDescription}-${coverage.codigo}` ===
            `${familyDescription.label}-${coverageValue}`
        );

      const coverageDescription = coverageList.find(
        (coverage) => String(coverage.value) === String(coverageValue)
      );

      setIsUploadObject(true);

      const newObject = {
        articuloRamaSecuencia: Number(quotationInfo?.arse),
        cobertura: Number(coverageValue),
        componente: Number(numberComponent),
        numeroCotizacion: Number(quotationInfo?.token),
        objetoAsegurado: {
          detalle: formFields[`detailObject-tab-${numberComponent}`],
          idSecuencial: currentTableIndex,
          marca: formFields[`markObject-tab-${numberComponent}`],
          modelo: formFields[`modelObject-tab-${numberComponent}`],
          numeroDeSerie: formFields[`numberSerieObject-tab-${numberComponent}`],
          objeto: formFields[`descriptionObject-tab-${numberComponent}`],
          sumaAsegurada: Number(
            formFields[`sumInsured-tab-${numberComponent}`]
          ),
        },
        rama: Number(quotationInfo?.branch),
        riesgo: String(coverageData?.riskCode),
      };

      formFields[
        `coverage-tab-${numberComponent}`
      ] = `${familyDescription?.label} - ${coverageDescription?.label}`;

      await QuotationService.postInsertInsuredObject(newObject);
      addNewBeneficiaryOnTable(formFields);
    } catch (error) {
      setCurrentTableIndex((prevValue) => prevValue - 1);
    } finally {
      setIsUploadObject(false);
    }
  };

  const onCancelObjectModal = () => {
    objectsForm?.resetFields();
    setCoverageList([]);
    setShowObjectModal(false);
  };

  const onHandleSelect = (familyCode: string) => {
    objectsForm?.resetFields([`coverage-tab-${numberComponent}`]);

    const filteredCoverages = Object.values(coveragesListInfo)
      .flat()
      .filter((coverage) => coverage.familyCode === familyCode);

    const updatedDescriptionList = filteredCoverages.map((coverage) => ({
      label: coverage.description,
      value: coverage.codigo,
    }));

    setCoverageList(updatedDescriptionList);
  };

  const createFamilyListFromCoverages = () => {
    const familyList = Array?.from(
      new Map(
        Object.values(coveragesListInfo)
          .flat()
          .map((coverage) => [
            `${coverage.familyDescription}-${coverage.familyCode}`,
            { label: coverage.familyDescription, value: coverage.familyCode },
          ])
      ).values()
    );

    setFamilyList(familyList);
  };

  useEffect(() => {
    if (coveragesListInfo) createFamilyListFromCoverages();
  }, [coveragesListInfo]);

  return (
    <ContentSection level='h3' title='Agregar Objeto'>
      <RowSize rowProps={{ gutter: [0, 15] }}>
        <RowSize rowProps={{ align: 'middle', justify: 'end', gutter: [0, 0] }}>
          <ActionButton
            className='btn-accent-outline-barbuss'
            icon={<PlusOutlined />}
            label='Agregar Objeto'
            onClick={() => setShowObjectModal(true)}
            type='secondary'
          />
        </RowSize>

        <Table
          bordered
          className='person-table'
          columns={MOCK_TABLE_COLUMNS_HEADER}
          dataSource={objectTable}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: objectTable?.length,
            showTotal: (total) =>
              total === 1 ? 'Un objeto' : `${total} objetos`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
          }}
          rowClassName={() => 'editable-row'}
          scroll={{ x: 'auto' }}
        />
      </RowSize>

      <Modal
        centered
        confirmText='Guardar'
        footer
        onCancel={onCancelObjectModal}
        onOk={onAddNewObject}
        open={showObjectModal}
        width={876}
      >
        <Form
          formProps={{
            form: objectsForm,
            name: 'object-form',
          }}
        >
          <Spin spinning={isUploadObject}>
            <RowSize rowProps={{ gutter: [0, 0] }}>
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  marginBottom: 10,
                  color: COLORS.secondary[100],
                }}
              >
                Nuevo objeto
              </Typography.Title>
            </RowSize>

            <RowSize className='mb-1'>
              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Familia:',
                    name: `family-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'familia',
                      rules: ['required'],
                    }),
                  }}
                  selectProps={{
                    disabled: !familyList?.length,
                    onSelect: onHandleSelect,
                    options: familyList,
                    showSearch: true,
                  }}
                />
              </ColSize>
              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Cobertura:',
                    name: `coverage-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'cobertura',
                      rules: ['required'],
                    }),
                  }}
                  selectProps={{
                    disabled: !coverageList?.length,
                    options: coverageList,
                    showSearch: true,
                  }}
                />
              </ColSize>
              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Descripción:',
                    name: `descriptionObject-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'descripción',
                      rules: ['required'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Marca:',
                    name: `markObject-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'marca',
                      rules: ['required'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Modelo:',
                    name: `modelObject-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'modelo',
                      rules: ['required'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputNumberForm
                  formProps={{
                    label: 'Número de Serie:',
                    name: `numberSerieObject-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'número de serie',
                      rules: ['required', 'number'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Detalle:',
                    name: `detailObject-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'detalle',
                      rules: ['required'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputCurrencyForm
                  formProps={{
                    label: 'Precio:',
                    name: `sumInsured-tab-${numberComponent}`,
                    rules: validateForm.generateRules({
                      label: 'precio',
                      rules: ['required', 'number'],
                    }),
                  }}
                />
              </ColSize>
            </RowSize>
          </Spin>
        </Form>
      </Modal>
    </ContentSection>
  );
};
